import { Component, computed, DestroyRef, inject, input } from '@angular/core'
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop'
import { AuthService } from '@app/modules/auth/services/auth.service'
import { WorkspaceRoleService } from '@app/modules/auth/services/workspace-role.service'
import { DataTableComponent } from '@app/modules/database/components/data-table/data-table.component'
import { DatabaseService } from '@app/modules/database/services/database.service'
import { FinancialDashboardComponent } from '@app/modules/events/components/financial-dashboard/financial-dashboard.component'
import { VenueCapacityPageComponent } from '@app/modules/venue-capacity/components/venue-capacity-page/venue-capacity-page.component'
import { Event } from '@app/v2/event/types/event.type'
import { MobileCollapsibleSectionComponent } from '@app/v2/shared/components/mobile-collapsible-section/mobile-collapsible-section.component'
import { WizardService } from '@app/v2/shared/components/wizard/services/wizard.service'
import { environment } from '@environments/environment'
import { EventTrackingCardComponent, type EventTrackingCardKey } from './event-tracking-card/event-tracking-card.component'

type EventTrackingCardItem = {
  key: EventTrackingCardKey
  title: string
  subtitle?: string
  loading?: boolean
}

type EventTrackingActionContext = {
  event: Event
  workspaceSlug?: string
  eventSlug?: string
}

@Component({
  selector: 'app-event-tracking',
  standalone: true,
  imports: [MobileCollapsibleSectionComponent, EventTrackingCardComponent],
  templateUrl: './event-tracking.component.html',
  styleUrl: './event-tracking.component.scss'
})
export class EventTrackingComponent {
  public readonly event = input.required<Event>()
  public readonly loadingCounts = input(false)
  public readonly readonly = input(false)
  public readonly archived = input(false)
  private readonly _auth = inject(AuthService)
  private readonly _workspaceRoleService = inject(WorkspaceRoleService)
  private readonly _userRole = toSignal(this._workspaceRoleService.getUserRole(), { initialValue: null })
  private readonly _wizardService = inject(WizardService)
  private readonly _databaseService = inject(DatabaseService)
  private readonly _destroyRef = inject(DestroyRef)

  /**
   * Actions to be performed when a card is clicked.
   * @param context {EventTrackingActionContext} - The context of the action.
   * @returns {void}
   */
  private readonly _cardActions: Record<EventTrackingCardKey, (context: EventTrackingActionContext) => void> = {
    database: ({ event }) => {
      this._databaseService
        .resetCounter(event.slug)
        .pipe(takeUntilDestroyed(this._destroyRef))
        .subscribe(() => {})
      this._wizardService.open({
        component: DataTableComponent,
        componentInputs: { eventId: event.id },
        headerConfig: { breadcrumbs: [event.name, 'Database'] }
      })
    },
    registration: ({ workspaceSlug, eventSlug }) => {
      if (!workspaceSlug || !eventSlug) return

      window.open(`${environment.dashboardsBaseUrl}marketing/${workspaceSlug}/${eventSlug}`, '_blank')
    },
    traffic: ({ workspaceSlug, eventSlug }) => {
      if (!workspaceSlug || !eventSlug) return

      window.open(`${environment.dashboardsBaseUrl}traffic/${workspaceSlug}/${eventSlug}/default`, '_blank')
    },
    capacity: ({ event }) => {
      this._wizardService.open({
        component: VenueCapacityPageComponent,
        componentInputs: { eventId: event.id },
        headerConfig: { breadcrumbs: [event.name, 'Venue Capacity'] }
      })
    },
    financial: ({ event }) => {
      this._wizardService.open({
        component: FinancialDashboardComponent,
        componentInputs: { eventId: event.id },
        headerConfig: { breadcrumbs: [event.name, 'Financial'] }
      })
    }
  }

  protected readonly cards = computed<EventTrackingCardItem[]>(() => [
    { key: 'database', title: 'View database', subtitle: this.eventRegistrationCounts(), loading: this.loadingCounts() },
    { key: 'registration', title: 'Registration tracking' },
    { key: 'traffic', title: 'On-site <br> traffic' },
    { key: 'capacity', title: 'Venue capacity' }
    // { key: 'financial', title: 'Financial tracking' }
  ])

  protected readonly cardReadonlyByKey = computed<Record<EventTrackingCardKey, boolean>>(() => {
    this._userRole()
    const canViewDatabase = this._workspaceRoleService.canViewDatabase()
    return {
      database: this.archived() || !canViewDatabase,
      registration: this.archived() || this.readonly(),
      traffic: this.archived() || this.readonly(),
      capacity: this.archived() || this.readonly(),
      financial: this.archived() || this.readonly()
    }
  })

  /**
   * Keeps the same registration count display behavior as legacy timeline cards.
   */
  private eventRegistrationCounts(): string {
    const event = this.event() as Event & { registration_count_display?: string; registration_count?: number | null }
    let count = '(0/0)'

    if (event.registration_count_display && event.registration_count_display !== '0') {
      count = `(${event.registration_count_display
        .split('/')
        .map((value) => value.replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1 '))
        .join('/')})`
    } else if (event.registration_count !== null && event.registration_count !== undefined && event.registration_count > 0) {
      count = `(${event.registration_count})`
    }

    return count
  }

  /**
   * Handles the selection of a card.
   * @param cardKey {EventTrackingCardKey} - The key of the card.
   * @returns {void}
   */
  protected onCardSelected(cardKey: EventTrackingCardKey): void {
    if (this.cardReadonlyByKey()[cardKey]) return
    const event = this.event()
    const actionContext: EventTrackingActionContext = {
      event,
      workspaceSlug: this._auth.currentWorkspace?.slug,
      eventSlug: event.slug ?? undefined
    }
    this._cardActions[cardKey]?.(actionContext)
  }
}
